import React, { useState, useEffect } from 'react';

const TrackingMap = ({ stops, driverPosition, routeProgress, isRealGpsStreaming, routePolyline, trafficInfo, geofenceReached }) => {
  const [animFrame, setAnimFrame] = useState(0);
  const [pulseFrame, setPulseFrame] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setAnimFrame(prev => (prev + 1) % 60);
    }, 100);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setPulseFrame(prev => (prev + 1) % 100);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  const pulseScale = 1 + Math.sin(animFrame * 0.2) * 0.15;
  const markerBounce = Math.sin(animFrame * 0.15) * 4; // px bounce for driver marker

  const LAT_MIN = -1.32, LAT_MAX = -1.26, LNG_MIN = 36.79, LNG_MAX = 36.85;
  const hasRealPosition = driverPosition?.lat != null && driverPosition?.lng != null;

  const driverLeft = hasRealPosition
    ? `${Math.min(90, Math.max(10, ((driverPosition?.lng - LNG_MIN) / (LNG_MAX - LNG_MIN)) * 100))}%`
    : `${30 + routeProgress * 0.45}%`;

  const driverTop = hasRealPosition
    ? `${Math.min(90, Math.max(10, (1 - (driverPosition?.lat - LAT_MIN) / (LAT_MAX - LAT_MIN)) * 100))}%`
    : `${80 - routeProgress * 0.6}%`;

  const polylinePoints = routePolyline?.length > 1
    ? routePolyline?.map(pt => ({
        x: Math.min(90, Math.max(10, ((pt?.lng - LNG_MIN) / (LNG_MAX - LNG_MIN)) * 100)),
        y: Math.min(90, Math.max(10, (1 - (pt?.lat - LAT_MIN) / (LAT_MAX - LAT_MIN)) * 100)),
      }))
    : null;

  const trafficColor = trafficInfo?.color || '#ffd700';

  return (
    <div className="relative w-full h-full bg-[#0d1f3c] overflow-hidden">
      {/* Map grid background */}
      <svg className="absolute inset-0 w-full h-full opacity-15" viewBox="0 0 400 500" preserveAspectRatio="xMidYMid slice">
        {[0, 40, 80, 120, 160, 200, 240, 280, 320, 360, 400, 440, 480]?.map(y => (
          <line key={`h${y}`} x1="0" y1={y} x2="400" y2={y} stroke="#ffd700" strokeWidth="0.5" />
        ))}
        {[0, 50, 100, 150, 200, 250, 300, 350, 400]?.map(x => (
          <line key={`v${x}`} x1={x} y1="0" x2={x} y2="500" stroke="#ffd700" strokeWidth="0.5" />
        ))}
        {/* Static roads */}
        <path d="M0 200 Q100 180 200 220 Q300 260 400 240" stroke="#ffd700" strokeWidth="3" fill="none" opacity="0.4" />
        <path d="M0 350 Q80 320 160 340 Q240 360 320 330 Q360 315 400 320" stroke="#ffd700" strokeWidth="2" fill="none" opacity="0.3" />
        <path d="M120 0 Q140 100 130 200 Q120 300 140 500" stroke="#ffd700" strokeWidth="2" fill="none" opacity="0.3" />
        <path d="M280 0 Q260 120 270 250 Q280 380 260 500" stroke="#ffd700" strokeWidth="2" fill="none" opacity="0.3" />
        {/* ORS route polyline or fallback */}
        {polylinePoints ? (
          <polyline
            points={polylinePoints?.map(p => `${p?.x * 4},${p?.y * 5}`)?.join(' ')}
            stroke={trafficColor}
            strokeWidth="4"
            fill="none"
            opacity="0.9"
          />
        ) : (
          <path
            d="M60 420 Q100 380 140 340 Q180 300 200 260 Q220 220 240 180 Q260 140 300 100"
            stroke={trafficColor}
            strokeWidth="4"
            fill="none"
            opacity="0.85"
            strokeDasharray="8 4"
          />
        )}
      </svg>

      {/* Map blocks */}
      <svg className="absolute inset-0 w-full h-full opacity-10" viewBox="0 0 400 500" preserveAspectRatio="xMidYMid slice">
        {[[20,60,60,40],[100,50,50,60],[200,70,70,50],[310,55,55,45],[30,150,80,35],[160,140,60,50],[260,160,70,40],[350,145,40,55],[20,280,55,45],[110,270,65,55],[220,285,50,40],[320,275,60,50]]?.map(([x,y,w,h], i) => (
          <rect key={i} x={x} y={y} width={w} height={h} fill="#ffd700" rx="3" />
        ))}
      </svg>

      {/* ─── PULSING PICKUP MARKER ─────────────────────────────────────────── */}
      <div className="absolute z-10" style={{ left: '15%', top: '82%', transform: 'translate(-50%, -100%)' }}>
        <div className="flex flex-col items-center">
          {/* Pulse rings */}
          <div className="relative">
            <div className="absolute -inset-4 rounded-full bg-green-400/20 animate-ping" style={{ animationDuration: '2s' }} />
            <div className="absolute -inset-2 rounded-full bg-green-400/30 animate-pulse" />
            <div className="w-8 h-8 rounded-full bg-green-500 border-3 border-white flex items-center justify-center shadow-lg z-10 relative" style={{ border: '3px solid white' }}>
              <svg viewBox="0 0 24 24" fill="white" className="w-4 h-4">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
              </svg>
            </div>
          </div>
          <div className="w-0.5 h-3 bg-green-400/60 mt-0.5"></div>
          <div className="bg-[#0a1628]/80 backdrop-blur-sm rounded-lg px-2 py-0.5 mt-0.5">
            <span className="text-green-400 text-[9px] font-bold uppercase tracking-wider">Pickup</span>
          </div>
        </div>
      </div>

      {/* ─── PULSING DROPOFF MARKER ────────────────────────────────────────── */}
      <div className="absolute z-10" style={{ left: '75%', top: '22%', transform: 'translate(-50%, -100%)' }}>
        <div className="flex flex-col items-center">
          {/* Geofence circle */}
          <div
            className={`absolute rounded-full border-2 -inset-6 ${geofenceReached ? 'border-green-400 bg-green-400/20' : 'border-[#ffd700]/40 bg-[#ffd700]/5'}`}
            style={{ transition: 'all 0.5s ease' }}
          />
          {/* Pulse rings */}
          <div className="relative">
            <div
              className="absolute -inset-3 rounded-full animate-ping"
              style={{
                background: geofenceReached ? 'rgba(34,197,94,0.25)' : 'rgba(255,215,0,0.2)',
                animationDuration: '1.8s',
              }}
            />
            <div className="w-9 h-9 rounded-full flex items-center justify-center shadow-lg z-10 relative" style={{ background: geofenceReached ? '#22c55e' : '#ffd700', border: '3px solid white' }}>
              <svg viewBox="0 0 24 24" fill={geofenceReached ? 'white' : '#0a1628'} className="w-4 h-4">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
              </svg>
            </div>
          </div>
          <div className="w-0.5 h-3 bg-[#ffd700]/60 mt-0.5"></div>
          <div className="bg-[#0a1628]/80 backdrop-blur-sm rounded-lg px-2 py-0.5 mt-0.5">
            <span className="text-[#ffd700] text-[9px] font-bold uppercase tracking-wider">Drop-off</span>
          </div>
        </div>
      </div>

      {/* ─── INTERMEDIATE STOP MARKERS ────────────────────────────────────── */}
      {stops?.slice(1, -1)?.map((stop, i) => {
        const positions = [
          { x: '38%', y: '62%' },
          { x: '58%', y: '42%' },
        ];
        const pos = positions?.[i] || { x: '50%', y: '50%' };
        const isCompleted = stop?.status === 'completed';
        const isActive = stop?.status === 'active';
        return (
          <div key={stop?.id} className="absolute -translate-x-1/2 -translate-y-full z-10" style={{ left: pos?.x, top: pos?.y }}>
            <div className="relative flex flex-col items-center">
              {isActive && <div className="absolute -inset-3 rounded-full animate-ping" style={{ background: 'rgba(255,215,0,0.3)' }} />}
              <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs font-bold shadow-lg z-10 ${
                isCompleted ? 'bg-green-500 border-green-300 text-white' : isActive ? 'bg-[#ffd700] border-white text-[#0a1628]' : 'bg-[#0a1628] border-white/40 text-white/60'
              }`}>
                {isCompleted ? '✓' : i + 2}
              </div>
              <div className="w-0.5 h-2 bg-white/40"></div>
            </div>
          </div>
        );
      })}

      {/* ─── ANIMATED DRIVER MARKER ───────────────────────────────────────── */}
      <div
        className="absolute -translate-x-1/2 -translate-y-1/2 z-20"
        style={{
          left: driverLeft,
          top: driverTop,
          transition: hasRealPosition ? 'left 1.5s cubic-bezier(0.4,0,0.2,1), top 1.5s cubic-bezier(0.4,0,0.2,1)' : 'left 2s ease, top 2s ease',
          transform: `translate(-50%, -50%) translateY(${markerBounce}px)`,
        }}
      >
        <div className="relative">
          {/* Outer pulse ring */}
          <div
            className="absolute -inset-5 rounded-full"
            style={{
              background: geofenceReached ? 'rgba(34,197,94,0.15)' : 'rgba(255,215,0,0.15)',
              transform: `scale(${pulseScale})`,
              transition: 'transform 0.1s ease',
            }}
          />
          {/* Inner glow */}
          <div
            className="absolute -inset-2 rounded-full"
            style={{
              background: geofenceReached ? 'rgba(34,197,94,0.25)' : 'rgba(255,215,0,0.25)',
              transform: `scale(${1 + Math.sin(animFrame * 0.3) * 0.1})`,
              transition: 'transform 0.1s ease',
            }}
          />
          {/* Driver icon */}
          <div
            className="w-11 h-11 rounded-full shadow-2xl flex items-center justify-center z-10 relative"
            style={{
              background: geofenceReached ? '#22c55e' : '#ffd700',
              border: '3px solid white',
              transform: driverPosition?.heading != null ? `rotate(${driverPosition?.heading}deg)` : 'none',
              transition: 'transform 0.5s ease, background 0.5s ease',
              boxShadow: `0 4px 20px ${geofenceReached ? 'rgba(34,197,94,0.5)' : 'rgba(255,215,0,0.5)'}`,
            }}
          >
            <svg viewBox="0 0 24 24" fill="#0a1628" className="w-5 h-5">
              <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z" />
            </svg>
          </div>
          {/* Direction arrow */}
          <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-0 h-0"
            style={{
              borderLeft: '5px solid transparent',
              borderRight: '5px solid transparent',
              borderBottom: `7px solid ${geofenceReached ? '#22c55e' : '#ffd700'}`,
            }}
          />
        </div>
      </div>

      {/* Map overlay gradient at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#0a1628]/50 to-transparent pointer-events-none" />

      {/* Live badge */}
      <div className="absolute top-4 left-4 flex items-center gap-1.5 bg-[#0a1628]/80 backdrop-blur-sm rounded-full px-3 py-1.5 border border-[#ffd700]/30">
        <div className={`w-2 h-2 rounded-full ${isRealGpsStreaming ? 'bg-green-400 animate-pulse' : 'bg-[#ffd700] animate-pulse'}`}></div>
        <span className="text-xs font-bold text-white">{isRealGpsStreaming ? 'GPS LIVE' : 'LIVE'}</span>
      </div>

      {/* Traffic badge */}
      {trafficInfo && (
        <div className="absolute top-4 left-24 flex items-center gap-1.5 bg-[#0a1628]/80 backdrop-blur-sm rounded-full px-2.5 py-1.5 border" style={{ borderColor: `${trafficInfo?.color}50` }}>
          <div className="w-2 h-2 rounded-full" style={{ background: trafficInfo?.color }} />
          <span className="text-xs font-bold" style={{ color: trafficInfo?.color }}>{trafficInfo?.label}</span>
        </div>
      )}

      {/* GPS coordinates badge */}
      {hasRealPosition && (
        <div className={`absolute top-4 right-14 flex items-center gap-1.5 bg-[#0a1628]/80 backdrop-blur-sm rounded-full px-3 py-1.5 border ${isRealGpsStreaming ? 'border-green-500/50' : 'border-green-500/30'}`}>
          {isRealGpsStreaming && (
            <div className="relative flex items-center justify-center mr-0.5">
              <div className="absolute w-3 h-3 rounded-full bg-green-400/30 animate-ping" />
              <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
            </div>
          )}
          <svg viewBox="0 0 24 24" fill="#4ade80" className="w-3 h-3">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
          </svg>
          <span className="text-xs font-bold text-green-400">GPS</span>
        </div>
      )}

      {/* Zoom controls */}
      <div className="absolute top-4 right-4 flex flex-col gap-1">
        <button className="w-8 h-8 bg-[#0a1628]/80 backdrop-blur-sm rounded-lg border border-white/10 text-white flex items-center justify-center text-lg font-light hover:bg-[#0a1628] transition-colors active:scale-95">+</button>
        <button className="w-8 h-8 bg-[#0a1628]/80 backdrop-blur-sm rounded-lg border border-white/10 text-white flex items-center justify-center text-lg font-light hover:bg-[#0a1628] transition-colors active:scale-95">−</button>
      </div>
    </div>
  );
};

export default TrackingMap;
